import { useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "@/hooks/useApi";
import { apiFetch, apiGet, apiSend } from "@/lib/api";
import { confirmDialog, toast } from "@/components/ui/feedback";

// 预设话术 (/knowledge/global-qa): 命名话术框 → N 段落 → 每段三人格独立讲解词 + 音频矩阵。
// 默认 1 段，按 + 添加段落 增加；每段顶部有 第 N 段 / 删除段。

type Variant = "concise" | "story" | "talky";

const VARIANTS: { key: Variant; label: string; voice: string }[] = [
  { key: "concise", label: "简约", voice: "小船长" },
  { key: "story",   label: "故事", voice: "金梅导游" },
  { key: "talky",   label: "话痨", voice: "鲟侠船长" },
];
const TRACK_LANGS: [string, string][] = [
  ["zh", "中文"],
  ["en", "英语"],
  ["ja", "日语"],
  ["ko", "韩语"],
  ["ru", "俄语"],
  ["fr", "法语"],
  ["es", "西语"],
  ["de", "德语"],
  ["it", "意语"],
  ["ar", "阿语"],
];

interface Phrase {
  id: string;
  name: string;
  note: string;
  seq: number;
  enabled: boolean;
  updatedAt: string;
}

interface SegmentRow {
  segIndex: number;
  scripts: Record<Variant, string>;
}

interface TrackRow {
  id: string;
  phraseId: string;
  voice: string;
  lang: string;
  text: string;
  audioUrl: string;
  durationSec: number;
  status: "pending" | "generating" | "ready" | "failed" | "stale";
  error: string;
  updatedAt: string;
}

function CreatePhraseModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(""); setNote(""); setBusy(false); } }, [open]);
  if (!open) return null;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { toast("请填写名字", "info"); return; }
    setBusy(true);
    try {
      await apiSend("POST", "/preset-phrases", { name: name.trim(), note: note.trim() });
      toast("已创建", "success");
      onCreated();
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "创建失败", "error");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-gray-900">新增话术</h3>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">名字 *</label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：欢迎语 / 兜底 / 唤醒回复" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">备注</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="可选，用途说明 / 触发条件等" rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">取消</button>
            <button type="submit" disabled={busy} className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60">{busy ? "创建中…" : "创建"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// 单段单 variant 的转音频面板：讲解词 + 试听 + 生成并缓存 + 多语言矩阵
function VariantPanel({
  phraseId, segIndex, variant, voice, label, scriptRemote, onScriptSaved,
}: {
  phraseId: string;
  segIndex: number;
  variant: Variant;
  voice: string;
  label: string;
  scriptRemote: string;
  onScriptSaved: () => void;
}) {
  const [script, setScript] = useState(scriptRemote);
  const [busy, setBusy] = useState("");
  const [tracks, setTracks] = useState<TrackRow[]>([]);
  const [selLangs, setSelLangs] = useState<string[]>(["zh"]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [playingTrackId, setPlayingTrackId] = useState<string>("");
  const playerRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => { setScript(scriptRemote); }, [phraseId, segIndex, variant, scriptRemote]);
  const dirty = script !== scriptRemote;
  const empty = script.trim() === "";

  const reload = async () => {
    try {
      const d = await apiGet<TrackRow[]>(`/preset-phrases/${phraseId}/segments/${segIndex}/tracks?variant=${variant}`);
      setTracks(d || []);
    } catch { /* keep */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [phraseId, segIndex, variant]);
  useEffect(() => {
    if (!tracks.some((t) => t.status === "generating")) return;
    const t = setInterval(reload, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tracks]);

  const saveScript = async () => {
    if (!dirty) return;
    setBusy("save");
    try {
      await apiSend("PUT", `/preset-phrases/${phraseId}/segments/${segIndex}/scripts/${variant}`, { script });
      toast("讲解词已保存", "success");
      onScriptSaved();
    } catch (e) { toast("保存失败: " + (e as Error).message, "error"); }
    finally { setBusy(""); }
  };
  const onPreview = async () => {
    if (empty) { toast("请先填写讲解词", "info"); return; }
    if (dirty) await saveScript();
    setBusy("preview");
    try {
      const res = await apiFetch(`/preset-phrases/${phraseId}/segments/${segIndex}/audio/preview?variant=${variant}`, { method: "POST" });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      new Audio(url).play().catch(() => {});
    } catch (e) { toast("试听失败: " + (e as Error).message, "error"); }
    finally { setBusy(""); }
  };
  const onGenerateZh = async () => {
    if (empty) { toast("请先填写讲解词", "info"); return; }
    if (dirty) await saveScript();
    setBusy("generate");
    try {
      await apiSend("POST", `/preset-phrases/${phraseId}/segments/${segIndex}/tracks/generate?variant=${variant}`, { langs: ["zh"] });
      toast("已下发生成", "success");
      await reload();
    } catch (e) { toast("生成失败: " + (e as Error).message, "error"); }
    finally { setBusy(""); }
  };
  const onGenerateSelected = async () => {
    if (selLangs.length === 0) { toast("请至少选择 1 种语言", "info"); return; }
    if (empty) { toast("请先填写讲解词", "info"); return; }
    if (dirty) await saveScript();
    try {
      await apiSend("POST", `/preset-phrases/${phraseId}/segments/${segIndex}/tracks/generate?variant=${variant}`, { langs: selLangs });
      toast("已下发生成任务", "success");
      await reload();
    } catch (e) { toast("生成失败: " + (e as Error).message, "error"); }
  };
  const onToggleTrack = (t: TrackRow) => {
    if (playingTrackId === t.id) { playerRef.current?.pause(); playerRef.current = null; setPlayingTrackId(""); return; }
    playerRef.current?.pause();
    const p = new Audio(t.audioUrl);
    playerRef.current = p;
    p.onended = () => { playerRef.current = null; setPlayingTrackId(""); };
    p.onerror = () => { toast("加载失败", "error"); setPlayingTrackId(""); };
    p.play().catch(() => setPlayingTrackId(""));
    setPlayingTrackId(t.id);
  };
  const onDeleteTrack = async (t: TrackRow) => {
    await apiSend("DELETE", `/preset-phrases/${phraseId}/tracks/${t.id}`);
    await reload();
  };
  const trackOf = (lang: string) => tracks.find((t) => t.lang === lang);
  const toggleLang = (l: string) => setSelLangs((s) => s.includes(l) ? s.filter((x) => x !== l) : [...s, l]);

  return (
    <div className="space-y-3">
      <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 bg-teal-50 text-teal-700 rounded text-xs font-medium">{label} · {voice}</span>
          {dirty && <span className="text-[11px] text-amber-500">⚠ 未保存</span>}
          <span className="ml-auto flex items-center gap-2 flex-wrap">
            <button onClick={saveScript} disabled={!dirty || !!busy} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-60">
              {busy === "save" ? "保存中…" : "保存"}
            </button>
            <button onClick={onPreview} disabled={!!busy} className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100 disabled:opacity-60">
              <i className="ri-play-circle-line mr-1" />{busy === "preview" ? "试听中…" : "试听"}
            </button>
            <button onClick={onGenerateZh} disabled={!!busy} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60">
              <i className="ri-magic-line mr-1" />{busy === "generate" ? "生成中…" : "生成并缓存（中文）"}
            </button>
          </span>
        </div>
        <textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          placeholder={`填写「${label}」版本的讲解词`}
          rows={4}
          className="w-full px-3 py-2 border border-gray-200 rounded text-sm resize-y focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
        />
        {previewUrl && <audio controls src={previewUrl} className="w-full h-8" />}
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="text-sm font-medium text-gray-900 mr-2">多语言音频</span>
          <span className="text-gray-500">语言：</span>
          {TRACK_LANGS.map(([code, lab]) => (
            <button key={code} onClick={() => toggleLang(code)} className={`px-2.5 py-1.5 rounded-full font-medium transition-colors ${selLangs.includes(code) ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"}`}>
              {lab}
            </button>
          ))}
          <span className="ml-auto flex items-center gap-2">
            <button onClick={() => setSelLangs(TRACK_LANGS.map(([c]) => c))} className="px-2.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-100">全选</button>
            <button onClick={() => setSelLangs([])} className="px-2.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-100">清除</button>
            <button onClick={onGenerateSelected} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700"><i className="ri-magic-line mr-1" />批量生成所选</button>
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 py-2 pr-3">语言</th>
                {TRACK_LANGS.map(([code, lab]) => (
                  <th key={code} className="text-left text-xs font-medium text-gray-500 py-2 pr-3">{lab}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-200">
                <td className="py-2.5 pr-3 text-gray-700 font-medium whitespace-nowrap">{voice}</td>
                {TRACK_LANGS.map(([code]) => {
                  const t = trackOf(code);
                  return (
                    <td key={code} className="py-2.5 pr-3">
                      {!t || t.status === "pending" ? (
                        <span className="text-xs text-gray-400">未生成</span>
                      ) : t.status === "generating" ? (
                        <span className="text-xs text-amber-600"><i className="ri-loader-4-line animate-spin mr-0.5" />生成中</span>
                      ) : t.status === "failed" ? (
                        <span className="text-xs text-red-500" title={t.error}>失败</span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <button onClick={() => onToggleTrack(t)} title={playingTrackId === t.id ? "停止" : "播放"} className={`w-6 h-6 flex items-center justify-center rounded-full text-white ${playingTrackId === t.id ? "bg-amber-500 hover:bg-amber-600" : "bg-teal-600 hover:bg-teal-700"}`}>
                            <i className={`text-xs ${playingTrackId === t.id ? "ri-pause-fill" : "ri-play-fill"}`} />
                          </button>
                          <span className={`text-xs ${t.status === "stale" ? "text-amber-600" : "text-gray-500"}`}>
                            {t.durationSec > 0 ? `${t.durationSec}s` : ""}
                            {t.status === "stale" && " 已过期"}
                          </span>
                          <button onClick={() => onDeleteTrack(t)} title="删除" className="text-gray-300 hover:text-red-500">
                            <i className="ri-close-line text-sm" />
                          </button>
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-400">讲解词修改保存后，已生成音频会标记「已过期」，重新批量生成即可覆盖；非中文语言先由 AI 翻译再合成。</p>
      </div>
    </div>
  );
}

// 单段：三人格 tab + 该 tab 的 VariantPanel + 删除段按钮
function SegmentBlock({
  phraseId, segIndex, ordinal, total, segment, onSegmentChanged, onSegmentDeleted,
}: {
  phraseId: string;
  segIndex: number;
  ordinal: number;
  total: number;
  segment: SegmentRow;
  onSegmentChanged: () => void;
  onSegmentDeleted: () => void;
}) {
  const [variant, setVariant] = useState<Variant>("concise");
  const active = VARIANTS.find((v) => v.key === variant)!;
  const del = async () => {
    if (total <= 1) { toast("至少保留一段", "info"); return; }
    if (!(await confirmDialog({ title: "删除段落", message: `删除第 ${ordinal} 段？该段所有讲解词和音频都会一起删除。`, confirmText: "删除" }))) return;
    await apiSend("DELETE", `/preset-phrases/${phraseId}/segments/${segIndex}`);
    toast("已删除", "success");
    onSegmentDeleted();
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">第 {ordinal} 段</span>
        <button onClick={del} className="ml-auto text-xs text-red-400 hover:text-red-600">删除段</button>
      </div>
      <div className="bg-gray-50 rounded-lg p-1">
        <div className="flex items-center gap-1">
          {VARIANTS.map((v) => (
            <button
              key={v.key}
              onClick={() => setVariant(v.key)}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${variant === v.key ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
              title={`配音：${v.voice}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>
      <VariantPanel
        key={`${segIndex}-${variant}`}
        phraseId={phraseId}
        segIndex={segIndex}
        variant={variant}
        voice={active.voice}
        label={active.label}
        scriptRemote={segment.scripts?.[variant] || ""}
        onScriptSaved={onSegmentChanged}
      />
    </div>
  );
}

function PhraseCard({ phrase, onNeedsReload }: { phrase: Phrase; onNeedsReload: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(phrase.name);
  const [editingName, setEditingName] = useState(false);
  const [note, setNote] = useState(phrase.note);
  const [busy, setBusy] = useState("");
  const [segments, setSegments] = useState<SegmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => { setName(phrase.name); setNote(phrase.note); }, [phrase.id, phrase.name, phrase.note]);
  const noteDirty = note !== phrase.note;

  const loadSegments = async () => {
    setLoading(true);
    try {
      const d = await apiGet<SegmentRow[]>(`/preset-phrases/${phrase.id}/segments`);
      setSegments(d || []);
    } catch (e) { toast("加载段落失败: " + (e as Error).message, "error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) loadSegments(); /* eslint-disable-next-line */ }, [open]);

  const renameSave = async () => {
    if (!name.trim() || name === phrase.name) { setEditingName(false); setName(phrase.name); return; }
    await apiSend("PUT", `/preset-phrases/${phrase.id}`, { name: name.trim() });
    setEditingName(false);
    onNeedsReload();
  };
  const saveNote = async () => {
    if (!noteDirty) return;
    setBusy("note");
    try {
      await apiSend("PUT", `/preset-phrases/${phrase.id}`, { note });
      toast("备注已保存", "success");
      onNeedsReload();
    } catch (e) { toast("保存失败: " + (e as Error).message, "error"); }
    finally { setBusy(""); }
  };
  const toggle = async () => {
    await apiSend("PUT", `/preset-phrases/${phrase.id}`, { enabled: !phrase.enabled });
    onNeedsReload();
  };
  const remove = async () => {
    if (!(await confirmDialog({ title: "删除话术", message: `删除「${phrase.name}」？该话术下所有段落、讲解词与音频都会一起删除。`, confirmText: "删除" }))) return;
    await apiSend("DELETE", `/preset-phrases/${phrase.id}`);
    toast("已删除", "success");
    onNeedsReload();
  };
  const addSegment = async () => {
    setAdding(true);
    try {
      await apiSend("POST", `/preset-phrases/${phrase.id}/segments`, {});
      await loadSegments();
    } catch (e) { toast("添加失败: " + (e as Error).message, "error"); }
    finally { setAdding(false); }
  };

  return (
    <div className={`bg-white border rounded-xl ${phrase.enabled ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
      <div className="flex items-center gap-3 p-4">
        <button onClick={() => setOpen(!open)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600">
          <i className={`ri-arrow-${open ? "down" : "right"}-s-line text-xl`} />
        </button>
        {editingName ? (
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={renameSave} onKeyDown={(e) => { if (e.key === "Enter") renameSave(); if (e.key === "Escape") { setEditingName(false); setName(phrase.name); } }} className="px-2 py-1 border border-gray-200 rounded text-sm" />
        ) : (
          <button onClick={() => { setEditingName(true); setName(phrase.name); }} className="text-base font-semibold text-gray-900 hover:text-teal-600">{phrase.name}</button>
        )}
        <span className="text-xs text-gray-400">更新于 {phrase.updatedAt}</span>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <button onClick={toggle} className="text-gray-500 hover:text-teal-600">{phrase.enabled ? "停用" : "启用"}</button>
          <button onClick={remove} className="text-red-400 hover:text-red-600">删除</button>
        </div>
      </div>
      {open && (
        <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50/50">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs text-gray-500">备注</span>
              {noteDirty && <button onClick={saveNote} disabled={busy === "note"} className="ml-auto text-xs text-teal-600 hover:text-teal-800 disabled:text-gray-300">{busy === "note" ? "保存中…" : "保存备注"}</button>}
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="（可选）用途说明 / 触发条件…" rows={2} className="w-full px-3 py-2 border border-gray-200 rounded text-sm resize-y focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
          </div>

          {loading ? (
            <div className="text-center py-6 text-sm text-gray-400">加载段落中…</div>
          ) : (
            <>
              {segments.map((seg, i) => (
                <SegmentBlock
                  key={seg.segIndex}
                  phraseId={phrase.id}
                  segIndex={seg.segIndex}
                  ordinal={i + 1}
                  total={segments.length}
                  segment={seg}
                  onSegmentChanged={loadSegments}
                  onSegmentDeleted={loadSegments}
                />
              ))}
              <button
                onClick={addSegment}
                disabled={adding}
                className="w-full px-3 py-2.5 border border-dashed border-gray-300 text-gray-500 rounded-xl text-sm font-medium hover:border-teal-300 hover:text-teal-600 hover:bg-teal-50/30 disabled:opacity-60"
              >
                <i className="ri-add-line mr-1" />{adding ? "添加中…" : "添加段落"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function GlobalQAPage() {
  const { data: phrases, reload } = useApi<Phrase[]>("/preset-phrases", []);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const visible = useMemo(() => phrases.filter((p) => search === "" || p.name.includes(search)), [phrases, search]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">预设话术</h1>
          <p className="text-sm text-gray-500 mt-1">命名话术框 × 多段落 × 三人格独立讲解词 × 多语言音频矩阵</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 shadow-sm">
          <i className="ri-add-line mr-1.5" />新增话术
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜索话术名" className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
        </div>
        <span className="text-xs text-gray-400">共 {phrases.length} 个话术</span>
      </div>

      <div className="space-y-3">
        {visible.map((p) => (
          <PhraseCard key={p.id} phrase={p} onNeedsReload={reload} />
        ))}
        {visible.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-sm text-gray-400">
            {phrases.length === 0 ? "还没有话术，点右上角「新增话术」开始" : "没有匹配的话术"}
          </div>
        )}
      </div>

      <CreatePhraseModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={reload} />
    </div>
  );
}

export default GlobalQAPage;
